// src/store/Slices/aiTradingSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../API/api';
import { Buffer } from 'buffer';

/** =========================
 *  Async Thunks
 *  ========================= */

export const fetchPriceAndPrediction = createAsyncThunk(
  'aiTrading/fetchPriceAndPrediction',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/ai-trades/price-prediction');
      return { pricePrediction: response.data.pricePrediction };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch price prediction');
    }
  }
);

export const payForPrediction = createAsyncThunk(
  'aiTrading/payForPrediction',
  async ({ user_id, ai_wallet_id }, { rejectWithValue }) => {
    try {
      const response = await api.post('/ai-trades/pay-for-prediction', { user_id, ai_wallet_id });
      return response.data; // { message, pricePrediction }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to pay for prediction');
    }
  }
);

export const startTrade = createAsyncThunk(
  'aiTrading/startTrade',
  async (tradeData, { rejectWithValue }) => {
    try {
      const response = await api.post('/ai-trades/trade', tradeData);
      return response.data; // trade object
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to start trade');
    }
  }
);

export const stopTrade = createAsyncThunk(
  'aiTrading/stopTrade',
  async (tradeId, { rejectWithValue }) => {
    try {
      const response = await api.post('/ai-trades/stop', { tradeId });
      // API returns: { message, trade: {...}, ... }  includes tax fields and net_profit on close
      return { tradeId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to stop trade');
    }
  }
);

export const cancelRemainingTrades = createAsyncThunk(
  'aiTrading/cancelRemainingTrades',
  async (tradeId, { rejectWithValue }) => {
    try {
      const response = await api.post('/ai-trades/cancel-remaining', { tradeId });
      // { message, trade: { remaining_trades } }
      return { tradeId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to cancel remaining trades');
    }
  }
);

export const fetchTrades = createAsyncThunk(
  'aiTrading/fetchTrades',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/ai-trades/trades');
      return response.data; // array of trades (includes profit_loss/tax/net if completed)
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to get trades');
    }
  }
);

export const fetchTradeImage = createAsyncThunk(
  'aiTrading/fetchTradeImage',
  async (tradeId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/ai-trades/trade/${tradeId}/share`, {
        responseType: 'arraybuffer',
      });
      const base64Image = Buffer.from(response.data, 'binary').toString('base64');
      return `data:image/png;base64,${base64Image}`;
    } catch (error) {
      // keep detailed diagnostics in logs but return a concise payload to UI
      console.error('Error fetching trade image:', {
        tradeId,
        message: error.message,
        code: error.code,
        response: error.response
          ? {
              status: error.response.status,
              headers: error.response.headers,
              data: error.response.data ? Buffer.from(error.response.data).toString('utf8') : null,
            }
          : null,
        requestUrl: `${api.defaults.baseURL}/ai-trades/trade/${tradeId}/share`,
      });
      return rejectWithValue({
        message: error.message || 'Failed to fetch trade image',
        status: error.response?.status,
        code: error.code,
      });
    }
  }
);

/** =========================
 *  Slice
 *  ========================= */

const aiTradingSlice = createSlice({
  name: 'aiTrading',
  initialState: {
    trades: [],
    currentPrice: null,
    pricePrediction: null,
    hasPaidForPrediction: false,
    lastFetched: 0,
    loading: false,
    error: null,
    tradeImage: null,
    tradeImageLoading: false,
  },
  reducers: {
    addTrade: (state, action) => {
      state.trades.push(action.payload);
    },
    updateTrade: (state, action) => {
      const index = state.trades.findIndex((t) => t._id === action.payload.tradeId);
      if (index !== -1) {
        state.trades[index] = {
          ...state.trades[index],
          status: action.payload.status ?? state.trades[index].status,
          profit_loss: action.payload.profit_loss ?? state.trades[index].profit_loss,
          reason: action.payload.reason ?? state.trades[index].reason,
          tax_rate: action.payload.tax_rate ?? state.trades[index].tax_rate,
          tax_amount: action.payload.tax_amount ?? state.trades[index].tax_amount,
          net_profit: action.payload.net_profit ?? state.trades[index].net_profit,
          liquidation_price: action.payload.liquidation_price ?? state.trades[index].liquidation_price,
          liquidated: action.payload.liquidated ?? state.trades[index].liquidated,
        };
      }
    },
    updateCurrentPrice: (state, action) => {
      state.currentPrice = action.payload;
    },
    updatePricePrediction: (state, action) => {
      state.pricePrediction = action.payload;
      state.hasPaidForPrediction = true;
      state.lastFetched = Date.now();
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetPaymentStatus: (state) => {
      state.hasPaidForPrediction = false;
      state.pricePrediction = null;
    },
    clearTradeImage: (state) => {
      state.tradeImage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /** fetchPriceAndPrediction */
      .addCase(fetchPriceAndPrediction.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchPriceAndPrediction.fulfilled, (state, action) => {
        state.loading = false;
        state.pricePrediction = action.payload.pricePrediction;
        state.hasPaidForPrediction = true;
        state.lastFetched = Date.now();
      })
      .addCase(fetchPriceAndPrediction.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
        if (action.payload === 'Payment required to view price prediction') {
          state.hasPaidForPrediction = false;
        }
      })

      /** payForPrediction */
      .addCase(payForPrediction.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(payForPrediction.fulfilled, (state, action) => {
        state.loading = false;
        state.pricePrediction = action.payload.pricePrediction;
        state.hasPaidForPrediction = true;
        state.lastFetched = Date.now();
      })
      .addCase(payForPrediction.rejected, (state, action) => {
        state.loading = false; state.error = action.payload; state.hasPaidForPrediction = false;
      })

      /** startTrade */
      .addCase(startTrade.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(startTrade.fulfilled, (state, action) => {
        state.loading = false;
        state.trades.push(action.payload);
      })
      .addCase(startTrade.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

      /** stopTrade */
      .addCase(stopTrade.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(stopTrade.fulfilled, (state, action) => {
        state.loading = false;
        const { tradeId, trade, message, reason } = action.payload;
        const idx = state.trades.findIndex((t) => t._id === tradeId || t._id === trade?._id);
        if (idx !== -1) {
          // Merge server trade object to preserve all computed fields (tax/net/liq/etc.)
          state.trades[idx] = {
            ...state.trades[idx],
            ...trade,
            status: 'Completed',
            reason: reason || state.trades[idx].reason || 'Stopped',
          };
        }
      })
      .addCase(stopTrade.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

      /** cancelRemainingTrades */
      .addCase(cancelRemainingTrades.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(cancelRemainingTrades.fulfilled, (state, action) => {
        state.loading = false;
        const { tradeId, trade } = action.payload;
        const idx = state.trades.findIndex((t) => t._id === tradeId || t._id === trade?._id);
        if (idx !== -1) {
          state.trades[idx] = {
            ...state.trades[idx],
            remaining_trades: trade?.remaining_trades ?? 0,
          };
        }
      })
      .addCase(cancelRemainingTrades.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

      /** fetchTrades */
      .addCase(fetchTrades.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchTrades.fulfilled, (state, action) => {
        state.loading = false; state.trades = action.payload || [];
      })
      .addCase(fetchTrades.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

      /** share image */
      .addCase(fetchTradeImage.pending, (state) => {
        state.tradeImageLoading = true; state.error = null;
      })
      .addCase(fetchTradeImage.fulfilled, (state, action) => {
        state.tradeImageLoading = false; state.tradeImage = action.payload;
      })
      .addCase(fetchTradeImage.rejected, (state, action) => {
        state.tradeImageLoading = false; state.error = action.payload?.message || action.payload;
      });
  },
});

export const {
  addTrade,
  updateTrade,
  updateCurrentPrice,
  updatePricePrediction,
  setError,
  clearError,
  resetPaymentStatus,
  clearTradeImage,
} = aiTradingSlice.actions;

export default aiTradingSlice.reducer;
